import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Tablet } from "lucide-react";
import { toast } from "sonner";

import { DeviceRouteLoadingDialog } from "~/components/devices/device-route-loading-dialog";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { convexClient } from "~/integrations/convex/provider";
import { toConvexTableId } from "~/lib/convex-table-id";
import { ProductEvents } from "~/lib/posthog";

const getDeviceByIdRef = api.posDevices.getDeviceById;

const deleteDeviceRef = api.posDevices.deleteDevice;

const deleteTerminalByDeviceRef =
  api.posDevicePaymentTerminal.deleteByPosDeviceId;

// V4.1: Route-based modal for URL context tracking
export const Route = createFileRoute(
  "/_authenticated/locations/$id/devices/$deviceId/delete"
)({
  component: DeleteDeviceModal,
});

function DeleteDeviceModal() {
  const { id: locationId, deviceId } = Route.useParams();
  const navigate = useNavigate();

  const { data: device, isPending } = useQuery({
    queryKey: ["posDevices", "getDeviceById", deviceId],
    queryFn: () => {
      return convexClient.query(getDeviceByIdRef, {
        id: toConvexTableId("posDevices", deviceId),
      });
    },
  });

  const deletePosDeviceMutation = useMutation({
    mutationFn: (data: { id: Id<"posDevices"> }) => {
      return convexClient.mutation(deleteDeviceRef, data);
    },
    onSuccess: () => {
      ProductEvents.deviceDeleted(deviceId);
    },
  });

  const deletePaymentTerminalMutation = useMutation({
    mutationFn: (data: { posDeviceId: Id<"posDevices"> }) => {
      return convexClient.mutation(deleteTerminalByDeviceRef, data);
    },
  });

  const handleClose = () => {
    void navigate({
      to: "/locations/$id/devices/$deviceId",
      params: { id: locationId, deviceId },
    });
  };

  const handleDelete = async () => {
    if (!device) {
      return;
    }
    if (
      deletePosDeviceMutation.isPending ||
      deletePaymentTerminalMutation.isPending
    ) {
      return;
    }

    try {
      await deletePosDeviceMutation.mutateAsync({ id: device._id });
      await deletePaymentTerminalMutation.mutateAsync({
        posDeviceId: device._id,
      });
      toast.success("Device deleted successfully");
      await navigate({
        to: "/locations/$id/devices",
        params: { id: locationId },
      });
    } catch {
      toast.error("Failed to delete device");
    }
  };

  if (isPending || !device) {
    return <DeviceRouteLoadingDialog onOpenChange={handleClose} />;
  }

  const isProcessing =
    deletePosDeviceMutation.isPending ||
    deletePaymentTerminalMutation.isPending;

  return (
    <Dialog onOpenChange={handleClose} open>
      <DialogContent className="max-w-md">
        <DialogHeader className="space-y-1">
          <DialogTitle className="h2 font-heading text-foreground text-center leading-9">
            Delete POS Device
          </DialogTitle>
          <DialogDescription className="text-foreground text-center">
            This permanently removes this POS device from the location.
          </DialogDescription>
        </DialogHeader>
        <div className="bg-muted flex w-full items-center space-x-3 rounded-2xl px-4 py-3">
          <Tablet className="size-8" />
          <p className="label text-foreground leading-tight">
            {device?.name ?? "Unknown Device"}
          </p>
        </div>
        <p className="text-foreground text-center">
          After deletion, this device must be set up again before it can be used
          at this or another location.
        </p>
        <DialogFooter className="w-full space-x-3">
          <Button
            className="w-full"
            onClick={handleClose}
            type="button"
            variant="outline"
          >
            Cancel
          </Button>
          <Button
            className="w-full"
            disabled={isProcessing}
            onClick={handleDelete}
            type="button"
            variant="destructive"
          >
            Delete POS Device
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
